/**
 * International Auctions — the co-branded band's palette (BXS-3627).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT FOUR GETTERS COPIED INTO THREE COMPONENTS
 *
 * The band appears on the sale catalogue, the house page and the lot page, and
 * the mockups give it the same five colours on all three
 * (`docs/external-cataloge/Bidxsell Catalogue - *.html`, `partners()`):
 *
 *     color   accent   accentFg     e.g. Tattersalls  #1B3A5F  #B99860  #152B44
 *
 * Resolving them in one place is what stops the three surfaces drifting apart
 * the next time a house is added.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT SECOND-GUESSES `brand_color_contrast`
 *
 * The band used to be a white panel with a 4px coloured edge, so the house
 * colour touched almost nothing and an unreadable pairing was invisible. Now
 * the band is FILLED, every word in it sits on `brand_color`, and the stored
 * ink is whatever an operator picked in a colour dialog.
 *
 * `tattersalls-online` was seeded #F07D1A with white ink: 2.6:1, well under the
 * 4.5:1 floor, and it would have shipped as a header nobody could read. That
 * seed is fixed, but the next one is a form field away, so the value is CHECKED
 * rather than trusted — the operator's choice is honoured whenever it is
 * legible, and only overridden when it is not.
 */

import { ExternalHouse } from '../_mock/international-auctions.models';

/** The resolved band palette. Every field is a usable CSS colour. */
export interface HouseBrand {
  /** The band's background — the house's main colour. */
  color: string;
  /** Readable ink on `color`: titles, meta, the wordmark. */
  ink: string;
  /** The second colour: eyebrow, outbound button, the rule under the band. */
  accent: string;
  /** Readable ink on `accent` — the button's own text. */
  accentInk: string;
  /** True when the logo already carries the house colour and needs no plaque. */
  logoOnBrand: boolean;
}

/** Used when a house record carries no colour at all. `$ia-dark`. */
const FALLBACK_BRAND = '#033a09';
const FALLBACK_INK = '#ffffff';

/** Candidates when a stored ink is too weak to read. Near-black, not pure. */
const INK_CANDIDATES = ['#ffffff', '#101010'];

/** WCAG 2.1 AA for body text. The band's meta line is 13–15px. */
const MIN_CONTRAST = 4.5;

export function resolveHouseBrand(house: ExternalHouse | null | undefined): HouseBrand {
  const color = clean(house?.brand_color) || FALLBACK_BRAND;
  const ink = readableOn(color, clean(house?.brand_color_contrast) || FALLBACK_INK);

  // No accent is a legitimate state, not a gap to paper over: falling back to
  // the ink gives a monochrome band that is plainly deliberate, where an
  // invented gold would look like a house's real colour and be wrong.
  const accent = clean(house?.brand_accent_color) || ink;
  const accentInk = readableOn(accent, clean(house?.brand_accent_contrast) || color);

  return {
    color,
    ink,
    accent,
    accentInk,
    logoOnBrand: house?.logo_on_brand === true,
  };
}

/**
 * `preferred` when it can be read on `background`, otherwise whichever of
 * white/near-black can.
 */
function readableOn(background: string, preferred: string): string {
  const bg = parseHex(background);

  // Not a hex we can reason about — `rgb()`, a named colour, a gradient. Leave
  // the operator's choice alone rather than overriding on a failed parse.
  if (bg === null) {
    return preferred;
  }

  if (contrast(bg, parseHex(preferred)) >= MIN_CONTRAST) {
    return preferred;
  }

  let best = preferred;
  let bestRatio = contrast(bg, parseHex(preferred));

  for (const candidate of INK_CANDIDATES) {
    const ratio = contrast(bg, parseHex(candidate));
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
  }

  return best;
}

function clean(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/** `#abc` and `#aabbcc`, with or without the hash. Null for anything else. */
function parseHex(value: string): [number, number, number] | null {
  const hex = clean(value).replace(/^#/, '');

  if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
    return null;
  }

  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => char + char)
          .join('')
      : hex;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** WCAG relative luminance. */
function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
function contrast(
  a: [number, number, number] | null,
  b: [number, number, number] | null
): number {
  if (a === null || b === null) {
    return 0;
  }

  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);

  return (light + 0.05) / (dark + 0.05);
}
